import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactPlayer from "react-player";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore"; 
import { LoginModal } from "./LoginModal";
import AdminPanel from "./AdminPanel";
import "./App.css";

const unselectable = {
  userSelect: "none",
  WebkitUserSelect: "none",
  msUserSelect: "none",
  MozUserSelect: "none",
  cursor: "default"
};

function App() {
  const [playlist, setPlaylist] = useState([]);
  const [displayPlaylist, setDisplayPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [showFallbackButton, setShowFallbackButton] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "" });
  const showToast = (msg) => {
    setToast({ visible: true, message: msg });
    setTimeout(() => setToast({ visible: false, message: "" }), 3000); // Verschwindet nach 3 Sekunden
  };
  
  const [debugLog, setDebugLog] = useState([]);
  const addLog = (msg) => {
    setDebugLog(prev => [`${new Date().toLocaleTimeString().split(' ')[0]}: ${msg}`, ...prev].slice(0, 8));
  }; 
  
  const playerRef = useRef(null);
  const lastChange = useRef(0);
  const [user, setUser] = useState(null);
  const [isEditor, setIsEditor] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [adminModeVisible, setAdminModeVisible] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const [debugVisible, setDebugVisible] = useState(false);
  const [liveClicks, setLiveClicks] = useState(0);
  const [newVideo, setNewVideo] = useState({ title: "", url: "", category: "all" });
  const [shareModalVisible, setShareModalVisible] = useState(false);

  const handleLiveClick = () => {
    const newCount = liveClicks + 1;
    setLiveClicks(newCount);
    if (newCount >= 7) {
      setDebugVisible(!debugVisible);
      setLiveClicks(0); // Reset
      addLog("DEBUG MODE: " + (!debugVisible ? "ON" : "OFF"));
    }
    // Reset des Zählers nach 3 Sekunden Inaktivität
    setTimeout(() => setLiveClicks(0), 3000);
  };


  // --- PWA INSTALL LOGIK START ---
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      addLog("PWA: Install Ready");
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    addLog(`PWA: Install ${outcome}`);
    setDeferredPrompt(null);
  };
  // --- PWA INSTALL LOGIK ENDE ---

  // User SHARE Funktion
  const handleUserShare = async (videoFromButton = null) => {
  // Wir priorisieren das Video, das direkt vom Button kommt
    const targetVideo = videoFromButton || displayPlaylist[currentIndex];
  
    if (!targetVideo || !targetVideo.id) {
      addLog("Share Error: Video Daten fehlen im Klick");
      return;
    }

    // WICHTIG: Hier muss das "try" stehen, damit das "catch" unten funktioniert
    try {
      const shareUrl = `https://tv.streetwise.studio/v/${targetVideo.id}`;
      const titleText = `streetwise TV`;
      const videoText = `Hey, ein Videoclip mit Dir erscheint LIVE in der neuen streetwise TV App. Klick einfach auf diesen Link hier dann kannst du es direkt anschauen: "${targetVideo.title || 'Video'}"`;

    // Zuerst versuchen wir das native Teilen (Handy)
      if (navigator.share) {
       await navigator.share({
          title: titleText,
          text: videoText,
          url: shareUrl,
        });
        addLog("System-Share Erfolg");
      } else {
    // Desktop oder Browser ohne Share-API -> Modal öffnen
        setShareModalVisible(true);
        addLog("Modal geöffnet");
      }
    } catch (err) {
    // Wenn der User den Share abbricht, ist das kein Fehler
      if (err.name !== 'AbortError') {
        addLog("Share Error: " + err.message);
        setShareModalVisible(true); // Fallback auf Modal
      }
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u && u.email === "procreaction@gmail.com") setIsEditor(true);
      else { setIsEditor(false); setAdminModeVisible(false); }
    });
    return unsub;
  }, []);

  // PLAYLIST
  useEffect(() => {
    // 1. URL auslesen
    const segments = window.location.pathname.split("/");
    const firstSegment = segments[1] ? decodeURIComponent(segments[1]) : null;
    const isDirectVideo = firstSegment === "v";
    const targetVideoId = isDirectVideo ? segments[2] : null;
    const urlCategory = !isDirectVideo ? firstSegment : null;

    const q = query(collection(db, "videos"), orderBy("order", "asc"));
  
    const unsub = onSnapshot(q, (snap) => {
      let allDocs = snap.docs.map(d => ({ 
        ...d.data(), 
        id: d.id  // Hier wird die ID fest vergeben
      }));
      addLog(`Docs geladen: ${allDocs.length}`);
      
      let filteredDocs = [...allDocs];
      let initialIndex = 0;

      // 2. Logik-Weiche: Kanal-Filter oder Direkt-Video?
      if (isDirectVideo && targetVideoId) {
        // Fall A: Direkt-Link -> Wir behalten alle Videos (damit man weiterschauen kann)
        const foundIndex = allDocs.findIndex(v => v.id === targetVideoId);
        if (foundIndex !== -1) {
          initialIndex = foundIndex;
          addLog(`Direkt-Match: ${allDocs[foundIndex].title}`);
          // NEU: Wir simulieren den Start, damit der Splash-Screen verschwindet
          setHasStarted(true); // Auto-Start bei Direkt-Link
          setIsMuted(true); // Sicherstellen, dass es lautlos startet (Browser-Policy)
        }
      } else if (urlCategory && urlCategory !== "" && urlCategory !== "all" && urlCategory !== "radio") {
        // Fall B: Kanal-Filterung
        filteredDocs = allDocs.filter(v => (v.category || "all") === urlCategory);
        addLog(`Kanal: ${urlCategory}`);
      } else {
        addLog("Kanal: ALL");
      }

      setPlaylist(filteredDocs);
    
      // 3. Anzeige-Playlist (Shuffle oder Sortiert)
      if (shuffleMode) {
        let shuffled = [...filteredDocs].sort(() => Math.random() - 0.5);
        
        // Wenn Direkt-Link: Ziel-Video an die erste Stelle der gemischten Liste schieben
        if (isDirectVideo && targetVideoId) {
          const videoObj = filteredDocs.find(v => v.id === targetVideoId);
          if (videoObj) {
            shuffled = [videoObj, ...shuffled.filter(v => v.id !== targetVideoId)];
          }
        }
        setDisplayPlaylist(shuffled);
        setCurrentIndex(0); 
      } else {
        setDisplayPlaylist(filteredDocs);
        // Bei normaler Sortierung den gefundenen Index nutzen (falls Direkt-Link)
        if (isDirectVideo) {
          setCurrentIndex(initialIndex);
        } else {
          setCurrentIndex(0);
        }
      }
    });

    return unsub;
  }, [shuffleMode]);

  useEffect(() => {
    let timer;
    const handleActivity = () => {
      setShowControls(true);
      clearTimeout(timer);
      timer = setTimeout(() => setShowControls(false), 3000);
    };
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("touchstart", handleActivity);
    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      clearTimeout(timer);
    };
  }, []);

  // NUR EINE handleNext Version:
  const handleNext = useCallback(() => {
    const now = Date.now();
    if (now - lastChange.current < 1500) return; 
    lastChange.current = now;
    
    addLog("Aktion: Next Video");
    setCurrentIndex((prev) => (prev + 1) % displayPlaylist.length);
    setIsBuffering(true);
    setShowFallbackButton(false);
    setHasStarted(true); 
  }, [displayPlaylist.length]);

  const toggleShuffle = () => {
    const currentVideo = displayPlaylist[currentIndex];
    if (!shuffleMode) {
      const shuffled = [...playlist].sort(() => Math.random() - 0.5);
      const newIndex = shuffled.findIndex(v => v.id === currentVideo.id);
      setDisplayPlaylist(shuffled);
      setCurrentIndex(newIndex !== -1 ? newIndex : 0);
    } else {
      const newIndex = playlist.findIndex(v => v.id === currentVideo.id);
      setDisplayPlaylist(playlist);
      setCurrentIndex(newIndex !== -1 ? newIndex : 0);
    }
    setShuffleMode(!shuffleMode);
  };

  const startStation = () => {
    addLog("Klick: StartStation"); 
    // --- NEU: URL Check für Deep Linking ---
    const params = new URLSearchParams(window.location.search);
    const videoIdFromUrl = params.get('v');
    let startingIndex = 0; // Standard: Erstes Video

    if (videoIdFromUrl) {
      const targetIndex = displayPlaylist.findIndex(vid => vid.id === videoIdFromUrl);
      if (targetIndex !== -1) {
        startingIndex = targetIndex;
        addLog("Deep Link gefunden: Video " + videoIdFromUrl);
      } else {
        addLog("Deep Link ID nicht in Playlist");
      }
    }
    // ---------------------------------------
    setShuffleMode(true);
    setCurrentIndex(startingIndex); // Nutzt jetzt entweder 0 oder die ID aus der URL
    
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch((e) => addLog("FS Error: " + e.message));
    }

    setTimeout(() => {
        addLog("SetHasStarted: ON"); 
        setHasStarted(true);
        setIsBuffering(true);
        setIsMuted(true); 
    }, 50);

    setTimeout(() => {
      addLog("Check: Läuft Video?");
      setShowFallbackButton(true); 
    }, 15000);
  };

  const manualKick = (e) => {
    if (e) e.stopPropagation();
    addLog("Manual Kick: FULL RESET");
    
    // 1. Alles auf Anfang
    setShowFallbackButton(false);
    setIsBuffering(true);
    
    // 2. Wir triggern einen harten Komponenten-Reset über die Playlist
    const shuffled = [...playlist].sort(() => Math.random() - 0.5);
    
    // Wir setzen den Index kurzzeitig auf einen Wert, der einen Re-Render erzwingt,
    // und laden die Playlist neu.
    setDisplayPlaylist([]); // Kurz leeren
    
    setTimeout(() => {
      setDisplayPlaylist(shuffled);
      setCurrentIndex(0);
      setHasStarted(true);
      addLog("System neu gestartet");
    }, 10); // Minimaler Delay für den "Reset-Effekt"
  };

  const enterFullScreenOnly = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.warn(`Fullscreen-Fehler: ${err.message}`);
      });
    }
  };

  const handleLogoClick = () => {
    const newCount = logoClicks + 1;
    setLogoClicks(newCount);
    if (newCount >= 5) {
      if (!user) setShowLogin(true);
      else if (isEditor) setAdminModeVisible(!adminModeVisible);
      setLogoClicks(0);
    }
  };

  // NUR NOCH EIN RETURN BLOCK:
  return (
    <div className="tv-container">
      {/* 1. Video-Bereich - Immer da, damit er laden kann */}
      <div className="video-wrapper"onDoubleClick={enterFullScreenOnly}>        
        <ReactPlayer
          ref={playerRef}
          key={displayPlaylist[currentIndex]?.id || 'loading'}
          url={displayPlaylist[currentIndex]?.url}
          className="react-player"
          playing={hasStarted} 
          muted={isMuted}
          width="100%"
          height="100%"
          onEnded={() => { addLog("Event: Ende"); handleNext(); }}
          onBuffer={() => { addLog("Status: Buffering..."); setIsBuffering(true); }}
          onBufferEnd={() => { addLog("Status: Buffer Ende"); setIsBuffering(false); }}
          onReady={() => {
            const type = displayPlaylist[currentIndex]?.url?.includes('vimeo') ? 'Vimeo' : 'YT';
            addLog(`Ready: ${type}`);
            setIsBuffering(false);
            if (hasStarted) {
              setTimeout(() => {
                const internal = playerRef.current?.getInternalPlayer();
                if (internal?.play) internal.play();
                else if (internal?.playVideo) internal.playVideo();
                addLog(`Kick: ${type}`);
              }, 150); 
            }
          }}
          onStart={() => { addLog("Aktion: Spielt!"); setIsBuffering(false); setShowFallbackButton(false); }} 
          playsinline={true}
          config={{ 
            youtube: { playerVars: { origin: window.location.origin, modestbranding: 1, controls: 0, autoplay: 1, mute: isMuted ? 1 : 0 } },
            vimeo: { playerOptions: { autoplay: true, muted: isMuted, autopause: false, background: false, texttrack: '' } }
          }}
          onError={(e) => { addLog("! ERROR !"); setTimeout(() => handleNext(), 1000); }}
        />
        <div className="interaction-overlay"></div>
      </div>

      {/* 2. HEADER */}
      <div className="header-container branding-permanent" style={{ zIndex: 100 }}>
        <div className="brand-section" onClick={handleLogoClick} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <h1 className="brand-name" style={{ ...unselectable, fontSize: "0.8rem", margin: 0 }}>
            STREETWISE TV
          </h1>
    
         {/* KANAL ANZEIGE DIREKT UNTER DEM TITEL */}
         {window.location.pathname.split("/")[1] && 
           window.location.pathname.split("/")[1] !== "v" && 
           window.location.pathname.split("/")[1] !== "all" && 
           window.location.pathname.split("/")[1] !== "radio" && (
            <div style={{ 
              color: "var(--streetwise-cyan)", 
              fontSize: "0.55rem", 
              fontWeight: "bold", 
              letterSpacing: "1px",
              marginTop: "1px",
              textTransform: "uppercase",
              ...unselectable
            }}>
              CH: {decodeURIComponent(window.location.pathname.split("/")[1])}
            </div>
          )}
        </div> {/* Schließt brand-section */}
        <div 
          className="live-indicator" 
          onClick={handleLiveClick} 
          style={{ ...unselectable, fontSize: "0.8rem", cursor: "pointer" }}
        >
          LIVE
        </div>
      </div> {/* Schließt header-container */}

 {/* 3. BEDINGTE ANZEIGE */}
 {!hasStarted ? (
        <div className="splash-screen" onClick={startStation} style={{ zIndex: 200 }}>
          <div className="splash-content">
            <img src="/favicon.png" alt="Logo" className="splash-favicon" style={{ width: "80px", height: "auto", marginBottom: "20px" }} />
            <h1 className="splash-logo-text">STREETWISE TV</h1>
            <div className="watch-now-container"><span className="watch-now-text">WATCH NOW</span></div>
          </div>
        </div>
      ) : (
        <>
          {isBuffering && (
            <div className="loading-overlay" style={{ zIndex: 50 }}>
              <div className="loading-content">
                <img src="/favicon.png" alt="Loading" className="loading-favicon" style={{ width: "69px", height: "auto", marginBottom: "15px" }} />
                <div className="loading-text" style={unselectable}>STREETWISE</div>
                {showFallbackButton && (
                  <button className="ready-btn" onClick={manualKick}>READY</button>
                )}
              </div>
            </div>
          )}

          {/* Mitte bleibt leer für das Lade-Overlay */}
          <div className={`action-overlay ${showControls ? 'visible' : 'hidden'}`} style={{ zIndex: 120 }}>
            {/* 1. APP INSTALLIEREN */}
            {deferredPrompt && (
              <button className="control-button btn-install" onClick={installApp}>
                INSTALL APP
              </button>
            )}
          </div>
          
          <div className={`controls-layer ${showControls ? 'controls-visible' : 'controls-hidden'}`} style={{ zIndex: 110 }}>
                                      
            {/* 2. ACTIVATE AUDIO */}
            <button className={`control-button btn-activate ${!isMuted ? 'active-audio' : ''}`} 
              onClick={() => {
                const newMutedState = !isMuted;
                setIsMuted(newMutedState);
                
                if (!newMutedState && playerRef.current) {
                  const AudioContext = window.AudioContext || window.webkitAudioContext;
                  if (AudioContext) {
                    const ctx = new AudioContext();
                    ctx.resume().then(() => {
                      setTimeout(() => {
                        try {
                          const internal = playerRef.current?.getInternalPlayer();
                          if (internal?.unMute) internal.unMute();
                          if (internal?.setVolume) internal.setVolume(1);
                          if (internal?.play) internal.play();
                          addLog("Audio Context resumed");
                        } catch (e) {
                          addLog("Unmute Error: " + e.message);
                        }
                      }, 300);
                    });
                  }
                }
              }}
            >
              {isMuted ? "ACTIVATE AUDIO" : "MUTE AUDIO"}
            </button>

            {/* 3. VIDEO TEILEN */}
            <button 
              className="control-button btn-share" 
              onClick={() => handleUserShare(displayPlaylist[currentIndex])} 
              title="Teilen"
            >
              🔗 SHARE
            </button>

            {/* 4. NEXT */}
            <button className="control-button btn-next" onClick={handleNext}>NEXT ❯</button>
          </div>
        </>
      )}

      {/* 4. DEBUG MONITOR */}
      {debugVisible && (
        <div style={{
          position: 'fixed', top: '50px', left: '10px', zIndex: 10000,
          background: 'rgba(0,0,0,0.9)', color: '#00ff00', fontSize: '10px', padding: '10px', 
          pointerEvents: 'none', fontFamily: 'monospace', border: '1px solid #00ff00', borderRadius: '4px'
        }}>
          <div style={{ color: '#fff', borderBottom: '1px solid #00ff00', marginBottom: '5px', fontWeight: 'bold' }}>
            DEBUG MONITOR
          </div>
          {/* ZEIGT DIE DATEN DES AKTUELLEN VIDEOS AN */}
          <div style={{ marginBottom: '8px', color: '#00f2ff' }}>
            CURRENT VIDEO:<br/>
            Title: {displayPlaylist[currentIndex]?.title || "N/A"}<br/>
            ID: {displayPlaylist[currentIndex]?.id || "MISSING"}<br/>
            Index: {currentIndex} / {displayPlaylist.length}
          </div>
          <div style={{ borderTop: '1px solid #333', paddingTop: '5px' }}>
            LOGS:
            {debugLog.map((log, i) => <div key={i} style={{opacity: 1 - (i*0.1)}}>{log}</div>)}
          </div>
        </div>
      )}

      {/* 5. ADMIN & LOGIN */}
      {adminModeVisible && isEditor && (
        <AdminPanel 
          playlist={playlist} 
          // NEU: Diese 4 Zeilen müssen hier rein:
          displayPlaylist={displayPlaylist}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          handleUserShare={handleUserShare}
          
          newVideo={newVideo} 
          setNewVideo={setNewVideo} 
          addVideo={async () => {
            if (!newVideo.title || !newVideo.url) return alert("Bitte Titel und URL ausfüllen");
            try {
              const { addDoc, collection } = await import("firebase/firestore");
              
              await addDoc(collection(db, "videos"), {
                title: newVideo.title,
                url: newVideo.url,
                // Standardmäßig jetzt "all"
                category: newVideo.category || "all", 
                order: playlist.length > 0 ? Math.max(...playlist.map(v => v.order)) + 1 : 0,
                createdAt: new Date()
              });
          
              // Reset auf "all"
              setNewVideo({ title: "", url: "", category: "all" });
              alert("Video zu 'all' hinzugefügt!");
            } catch (e) { 
              console.error("Upload Fehler:", e); 
            }
          }}
          moveVideo={async (index, direction) => {
            const newPlaylist = [...playlist].sort((a, b) => a.order - b.order);
            const targetIndex = direction === "up" ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= newPlaylist.length) return;
            const { updateDoc, doc } = await import("firebase/firestore");
            const itemA = newPlaylist[index];
            const itemB = newPlaylist[targetIndex];
            await updateDoc(doc(db, "videos", itemA.id), { order: itemB.order });
            await updateDoc(doc(db, "videos", itemB.id), { order: itemA.order });
          }}
          onClose={() => setAdminModeVisible(false)}
          onLogout={() => { auth.signOut(); setAdminModeVisible(false); }}
        />
      )}

      {showLogin && <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />}
      
      {/* Das Modal muss INSIDE das letzte div, damit React nicht meckert */}
      {shareModalVisible && (
        <div className="share-modal-overlay" onClick={() => setShareModalVisible(false)}>
          <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>VIDEO TEILEN</h3>
            <div className="share-options-grid">
              
              {/* WHATSAPP */}
              <button onClick={() => {
                const videoToShare = displayPlaylist[currentIndex];
                const url = `https://tv.streetwise.studio/v/${videoToShare.id}`;
                const msg = `STREETWISE TV\nCheck das mal aus: "${videoToShare.title}"\n\n${url}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
              }}>
                WHATSAPP
              </button>

              {/* TELEGRAM (Jetzt mit korrekter Definition) */}
              <button onClick={() => {
                const videoToShare = displayPlaylist[currentIndex];
                const url = `https://tv.streetwise.studio/v/${videoToShare.id}`;
                const msg = `STREETWISE TV\nCheck das mal aus: "${videoToShare.title}"`;
                window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(msg)}`, '_blank');
              }}>
                TELEGRAM
              </button>

              {/* E-MAIL */}
              <button onClick={() => {
                const videoToShare = displayPlaylist[currentIndex];
                const url = `https://tv.streetwise.studio/v/${videoToShare.id}`;
                const body = `STREETWISE TV\nCheck das mal aus: "${videoToShare.title}"\n\n${url}`;
                window.location.href = `mailto:?subject=STREETWISE TV&body=${encodeURIComponent(body)}`;
              }}>
                E-MAIL
              </button>

              {/* LINK KOPIEREN (URL repariert) */}
              <button onClick={async () => {
                const videoToShare = displayPlaylist[currentIndex];
                const url = `https://tv.streetwise.studio/v/${videoToShare.id}`;
                try {
                  await navigator.clipboard.writeText(url);
                  alert("Link in Zwischenablage kopiert!");
                } catch (err) {
                  console.error("Copy failed");
                }
              }}>
                LINK KOPIEREN
              </button>

            </div>
            <button className="close-share-btn" onClick={() => setShareModalVisible(false)}>SCHLIESSEN</button>
          </div>
        </div>
      )}
      {toast.visible && (
        <div className="streetwise-toast">
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default App;